// @flow
// React Router v6 compatibility shim for class components that expect v5 router props.
// Usage: export default withRouter(connect(mapStateToProps, mapDispatchToProps)(MyComponent));
import React, { useEffect, useRef, useCallback } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';

const withRouter = (WrappedComponent: any) => {
    const WithRouterWrapper = (props: any) => {
        const location = useLocation();
        const params = useParams();
        const navigate = useNavigate();
        const listenersRef = useRef<any[]>([]);

        // Provide history.listen() for class components that subscribe to navigation changes
        const listen = useCallback((callback: Function) => {
            listenersRef.current.push(callback);
            return () => {
                listenersRef.current = listenersRef.current.filter((cb: Function) => cb !== callback);
            };
        }, []);

        // Notify listeners on location change
        useEffect(() => {
            listenersRef.current.forEach((cb: Function) => cb(location, 'PUSH'));
        }, [location]);

        const history = {
            push: (path: string, state?: any) => navigate(path, { state }),
            replace: (path: string, state?: any) => navigate(path, { replace: true, state }),
            goBack: () => navigate(-1),
            listen,
        };

        const match = { params };

        return (
            <WrappedComponent
                {...props}
                location={location}
                match={match}
                params={params}
                history={history}
                navigate={navigate}
            />
        );
    };

    WithRouterWrapper.displayName = `withRouter(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;

    return WithRouterWrapper;
};

export default withRouter;
